import { useQuery, useQueryClient } from 'react-query';

import { useCompany } from 'modules/app/hooks/useCompany';
import { getV2ApiCompanyIdRequestsToApprove } from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdRequestsToApproveResponseOK } from 'src/client/spendesk-api-types';

export type RequestToApprove =
  GetV2ApiCompanyIdRequestsToApproveResponseOK['data']['requests'][0];

export type RequestsToApprove =
  GetV2ApiCompanyIdRequestsToApproveResponseOK['data'];

export const useRequestsToApproveQueryKey = ['v2', 'requests', 'to-approve'];
export const useRequestsToApproveQuery = ({
  enabled,
  orderingKey = 'createdAt',
  orderingOrder = 'desc',
  enrichRequestSpendLimitStatus = false,
}: {
  enabled: boolean;
  orderingKey?: 'createdAt';
  orderingOrder?: 'asc' | 'desc';
  enrichRequestSpendLimitStatus?: boolean;
}) => {
  const company = useCompany();
  return useQuery(
    useRequestsToApproveQueryKey,
    async (): Promise<{ data: RequestsToApprove }> => {
      const response = await getV2ApiCompanyIdRequestsToApprove({
        path: {
          companyId: company.id,
        },
        query: {
          orderingKey,
          orderingOrder,
          enrichRequestSpendLimitStatus: enrichRequestSpendLimitStatus
            ? 'true'
            : 'false',
        },
      });

      if (response.statusCode !== 200) {
        throw new Error('Failed to fetch requests to approve');
      }
      return response.body;
    },
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

export const useRemoveRequestsFromRequestsToApproveCache = () => {
  const queryClient = useQueryClient();

  const removeRequestsFromRequestsToApproveCache = (requestIds: string[]) => {
    const data = queryClient.getQueryData<{ data: RequestsToApprove }>(
      useRequestsToApproveQueryKey,
    );
    if (data) {
      queryClient.setQueryData<{ data: RequestsToApprove } | undefined>(
        useRequestsToApproveQueryKey,
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const updatedRequests = oldData.data.requests.filter(
            (request) => !requestIds.includes(request.id),
          );
          return {
            data: {
              ...oldData.data,
              requests: updatedRequests,
            },
          };
        },
      );
    }
  };

  return {
    removeRequestsFromRequestsToApproveCache,
  };
};

export const useUpdateFieldForRequestInToApprove = () => {
  const queryClient = useQueryClient();

  const updateDescriptionForRequest = ({
    requestId,
    description,
  }: {
    requestId: string;
    description: string;
  }) => {
    const data = queryClient.getQueryData<{ data: RequestsToApprove }>(
      useRequestsToApproveQueryKey,
    );
    if (data) {
      queryClient.setQueryData<{ data: RequestsToApprove } | undefined>(
        useRequestsToApproveQueryKey,
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const updatedRequests = oldData.data.requests.map((request) => {
            if (request.id === requestId) {
              return {
                ...request,
                description,
              };
            }
            return request;
          });

          return {
            data: {
              ...oldData.data,
              requests: updatedRequests,
            },
          };
        },
      );
    }
  };

  return {
    updateDescriptionForRequest,
  };
};
